/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-24
 */

import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Send, Close, Info, Warning } from '@mui/icons-material';

// Add local dependencies.
import {
  InviteMemberFormProps,
  InvitationFormData,
  SendInvitationRequest,
  InvitationFormErrors,
} from '../../../types/invitation';
import { StudioRole } from '../../../types/studio';
import {
  useSendInvitationMutation,
  useGetStudioRolesQuery,
} from '../../../features/studios/studiosApi';
import { useStudioPermissions } from '../../../hooks/useStudioPermissions';
import { useStudioUserLimits } from '../../../hooks/useSubscriptionLimits';
import { useGetCurrentSubscriptionQuery } from '../../../features/auth/authApi';
import { validateEmailTranslated } from '../../../utils/validationTranslated';
import {
  parseInvitationError,
  getDisplayErrorMessage,
  getErrorGuidance,
  isAlreadyMemberError,
  isPendingInvitationError,
} from '../../../utils/invitationErrors';
import UpgradePrompt from '../../subscription/UpgradePrompt';

/**
 * InviteMemberForm component provides a form for sending invitations
 * to new studio members with role selection and custom messages.
 */
const InviteMemberForm = ({
  studioId,
  onSuccess,
  onCancel,
  open = false,
  currentMemberCount = 0,
}: InviteMemberFormProps) => {
  const { t } = useTranslation('studio');
  const [formData, setFormData] = useState<InvitationFormData>({
    email: '',
    role: StudioRole.MEMBER,
    message: '',
  });
  const [errors, setErrors] = useState<InvitationFormErrors>({});
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  const [sendInvitation, { isLoading, error }] = useSendInvitationMutation();
  const { permissions, getAssignableRoles } = useStudioPermissions();

  // Subscription and limit checking
  const { data: subscription } = useGetCurrentSubscriptionQuery();
  const { canAddUser, remainingUsers, isAtUserLimit, userLimitPercent } =
    useStudioUserLimits(studioId, currentMemberCount);

  // Parse API errors and update form field errors
  useEffect(() => {
    if (error) {
      const apiErrors = parseInvitationError(error);
      setErrors(prev => ({
        ...prev,
        ...apiErrors,
      }));
    }
  }, [error]);

  // Fetch available roles from API
  const { data: rolesData, isLoading: rolesLoading } = useGetStudioRolesQuery();

  // Filter roles to the ones the current user may assign (see getAssignableRoles)
  const assignableRoles = useMemo(() => {
    if (!rolesData?.roles) return [];

    // Get roles that the current user can assign
    const userAssignableRoles = getAssignableRoles();

    // Filter API roles to only include those the user can assign
    const availableRoles = rolesData.roles
      .map(roleOption => roleOption.value as StudioRole)
      .filter(role => userAssignableRoles.includes(role));

    return availableRoles;
  }, [rolesData, getAssignableRoles]);

  // Helper function to get role display name from API data
  const getRoleDisplayName = (roleValue: StudioRole): string => {
    if (!rolesData?.roles) return roleValue;
    const roleOption = rolesData.roles.find(r => r.value === roleValue);
    return roleOption?.label || roleValue;
  };

  const handleInputChange =
    (field: keyof InvitationFormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target;
      setFormData(prev => ({ ...prev, [field]: value }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };

  const handleRoleChange = (event: any) => {
    const role = event.target.value as StudioRole;
    setFormData(prev => ({ ...prev, role }));

    // Clear role error
    if (errors.role) {
      setErrors(prev => ({ ...prev, role: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof InvitationFormData, string>> = {};

    // Email validation with translation support
    const emailError = validateEmailTranslated(formData.email, t);
    if (emailError) {
      newErrors.email = emailError;
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = t('invitations.invite.role.required');
    } else if (!assignableRoles.includes(formData.role)) {
      newErrors.role = t('invitations.invite.role.cannotAssign');
    }

    // Message validation (optional but has length limit)
    if (formData.message && formData.message.length > 500) {
      newErrors.message = t('invitations.invite.message.tooLong');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const invitationRequest: SendInvitationRequest = {
        studioId,
        email: formData.email.trim(),
        role: formData.role,
        message: formData.message.trim() || undefined,
      };

      const result = await sendInvitation(invitationRequest).unwrap();

      // Reset form
      setFormData({
        email: '',
        role: StudioRole.MEMBER,
        message: '',
      });
      setErrors({});

      // Call success callback
      onSuccess?.(result);
    } catch (err) {
      // Error is handled by RTK Query and displayed via the error state
      console.error('Failed to send invitation:', err);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      email: '',
      role: StudioRole.MEMBER,
      message: '',
    });
    setErrors({});
    onCancel?.();
  };

  const isFormValid =
    formData.email.trim() && formData.role && !isLoading && canAddUser;

  // Handle upgrade prompt
  const handleUpgradePromptClose = () => {
    setShowUpgradePrompt(false);
  };

  const handleUpgrade = (_planSlug: string) => {
    // Close the upgrade prompt and invitation form
    setShowUpgradePrompt(false);
    handleClose();
    // Navigation to pricing will be handled by UpgradePrompt component
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit,
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Send color="primary" />
            <Typography variant="h6">
              {t('invitations.invite.title')}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {/* Enhanced Error Alert with Guidance */}
            {error && errors.general && (
              <Alert severity="error">
                {getDisplayErrorMessage(error)}
                {getErrorGuidance(error) && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Info fontSize="small" />
                      {getErrorGuidance(error)}
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}

            {/* Specific guidance for "already a member" errors */}
            {error && isAlreadyMemberError(error) && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  {t('invitations.invite.alreadyMemberTip')}
                </Typography>
              </Alert>
            )}

            {/* Specific guidance for "pending invitation" errors */}
            {error && isPendingInvitationError(error) && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  {t('invitations.invite.pendingInvitationTip')}
                </Typography>
              </Alert>
            )}

            {/* Email Field */}
            <TextField
              label={t('invitations.invite.email.label')}
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              required
              fullWidth
              autoFocus
              placeholder={t('invitations.invite.email.placeholder')}
              disabled={isLoading}
            />

            {/* Role Selection */}
            <FormControl fullWidth required error={!!errors.role}>
              <InputLabel>{t('invitations.invite.role.label')}</InputLabel>
              <Select
                value={formData.role}
                onChange={handleRoleChange}
                label={t('invitations.invite.role.label')}
                disabled={isLoading || rolesLoading}
              >
                {rolesLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    {t('invitations.invite.role.loadingRoles')}
                  </MenuItem>
                ) : (
                  assignableRoles.map(role => (
                    <MenuItem key={role} value={role}>
                      {getRoleDisplayName(role)}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.role && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 2 }}
                >
                  {errors.role}
                </Typography>
              )}
            </FormControl>

            {/* Custom Message */}
            <TextField
              label={t('invitations.invite.message.label')}
              multiline
              rows={3}
              value={formData.message}
              onChange={handleInputChange('message')}
              error={!!errors.message}
              helperText={
                errors.message ||
                t('invitations.invite.message.helper', {
                  count: formData.message.length,
                })
              }
              placeholder={t('invitations.invite.message.placeholder')}
              fullWidth
              disabled={isLoading}
            />

            {/* Member Limit Warning */}
            {subscription && isAtUserLimit && (
              <Alert severity="error" icon={<Warning />}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('invitations.invite.limit.reached')}
                </Typography>
                <Typography variant="body2">
                  {t('invitations.invite.limit.reachedMessage', {
                    limit: subscription.plan.max_users_per_studio,
                    planName: subscription.plan.name,
                  })}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowUpgradePrompt(true)}
                  sx={{ mt: 1 }}
                >
                  {t('invitations.invite.limit.upgradeButton')}
                </Button>
              </Alert>
            )}

            {/* Member Limit Approaching Warning */}
            {subscription && !isAtUserLimit && userLimitPercent >= 80 && (
              <Alert severity="warning" icon={<Info />}>
                <Typography variant="body2">
                  <strong>{t('invitations.invite.limit.approaching')}</strong>{' '}
                  {t('invitations.invite.limit.approachingMessage', {
                    current: currentMemberCount,
                    limit: subscription.plan.max_users_per_studio,
                    percent: Math.round(userLimitPercent),
                  })}
                </Typography>
                {remainingUsers === 1 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('invitations.invite.limit.onlyOneRemaining')}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Permission Info */}
            {!permissions?.canInviteMembers && (
              <Alert severity="warning">
                {t('invitations.invite.noPermission')}
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            startIcon={<Close />}
          >
            {t('invitations.invite.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isFormValid}
            startIcon={
              isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Send />
              )
            }
          >
            {isLoading
              ? t('invitations.invite.sending')
              : t('invitations.invite.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upgrade Prompt Dialog */}
      <UpgradePrompt
        open={showUpgradePrompt}
        onClose={handleUpgradePromptClose}
        title="Upgrade to Add More Members"
        message="Increase your member limit by upgrading to a higher plan."
        limitType="member"
        currentUsage={{
          users: currentMemberCount,
          users_limit: subscription?.plan.max_users_per_studio,
        }}
        onUpgrade={handleUpgrade}
      />
    </>
  );
};

export default InviteMemberForm;
